/**
 * Layout maths of `NxMonthView`'s `lanes` layout and of the time grid's
 * capped all-day band: which bars and lines a week row has room for, and how
 * many entries each day hides behind „+N weitere“. Pure, no DOM.
 *
 * Bars (all-day and day-long entries) keep one lane across all their days of
 * the row, so a bar never bends; timed entries are lines that fill the free
 * lanes of their own day. With more entries than lanes the last lane of that
 * day holds the „+N“ instead, so a bar in the last lane is only drawn when
 * none of its days overflows.
 */
import {
  addLocalDays,
  compareEvents,
  effectiveEndMs,
  eventsOnDay,
  fromIsoDate,
  hasValidStart,
} from './eventDays';
import { belongsInLane, layoutAllDayLane, type NxTimeGridLaneItem } from './timeGridLayout';
import type { NxCalendarEvent } from './types';

/** What a lane of a day shows: a bar (index into `bars`), a timed line or nothing. */
export type NxMonthSlot = { kind: 'bar'; bar: number } | { kind: 'line'; event: NxCalendarEvent };

export interface NxMonthDayLayout {
  /** One entry per lane, `null` for a free lane. */
  slots: Array<NxMonthSlot | null>;
  /** Entries of the day that did not fit. */
  hidden: number;
}

export interface NxMonthWeekLayout {
  /** The bars drawn, with their lane in `row`. */
  bars: NxTimeGridLaneItem[];
  /** Per date of the row, in order. */
  days: NxMonthDayLayout[];
}

/**
 * Of packed bars, the ones that fit `capacity` lanes, and per column how many
 * do not. `extraPerColumn` counts further entries a column must show (the
 * month's timed lines): they push a column into overflow too.
 */
export function capLanes(
  items: readonly NxTimeGridLaneItem[],
  columns: number,
  capacity: number,
  extraPerColumn: readonly number[] = []
): { shown: NxTimeGridLaneItem[]; hidden: number[]; overflow: boolean[] } {
  const limit = Math.max(1, Math.floor(capacity));
  const covers = (item: NxTimeGridLaneItem, column: number) =>
    item.column <= column && item.column + item.span > column;
  const overflow = Array.from(
    { length: columns },
    (_, column) =>
      items.filter((item) => covers(item, column)).length + (extraPerColumn[column] ?? 0) > limit
  );
  const shown: NxTimeGridLaneItem[] = [];
  const hidden = Array.from({ length: columns }, () => 0);
  for (const item of items) {
    const lastLaneBlocked =
      item.row + 1 === limit && overflow.slice(item.column, item.column + item.span).some(Boolean);
    if (item.row >= limit || lastLaneBlocked) {
      for (let column = item.column; column < item.column + item.span; column += 1) {
        if (column < columns) hidden[column] += 1;
      }
      continue;
    }
    shown.push(item);
  }
  return { shown, hidden, overflow };
}

/**
 * One week row of the month: bars across their days, timed lines in the free
 * lanes of their day, `capacity` lanes per day.
 */
export function layoutMonthWeek(
  allEvents: readonly NxCalendarEvent[],
  dates: readonly string[],
  capacity: number
): NxMonthWeekLayout {
  const limit = Math.max(1, Math.floor(capacity));
  // One cheap pass keeps only the entries touching this row, so the per-day
  // scans below do not walk the whole (possibly huge) list seven times.
  const first = dates.length ? fromIsoDate(dates[0]) : null;
  const last = dates.length ? fromIsoDate(dates[dates.length - 1]) : null;
  const from = first?.getTime() ?? 0;
  const to = last ? addLocalDays(last, 1).getTime() : 0;
  const events =
    first && last
      ? allEvents.filter(
          (event) =>
            hasValidStart(event) && event.start.getTime() < to && effectiveEndMs(event) > from
        )
      : allEvents;
  const lines = dates.map((iso) => {
    const day = fromIsoDate(iso);
    return day ? eventsOnDay(events, day).filter((event) => !belongsInLane(event)) : [];
  });
  const packed = layoutAllDayLane(events, dates).items;
  const { shown, hidden, overflow } = capLanes(
    packed,
    dates.length,
    limit,
    lines.map((list) => list.length)
  );

  const days: NxMonthDayLayout[] = dates.map((_, column) => ({
    slots: Array.from({ length: limit }, () => null),
    hidden: hidden[column],
  }));
  shown.forEach((bar, index) => {
    for (let column = bar.column; column < bar.column + bar.span; column += 1) {
      const day = days[column];
      if (day) day.slots[bar.row] = { kind: 'bar', bar: index };
    }
  });
  lines.forEach((list, column) => {
    const day = days[column];
    const usable = overflow[column] ? limit - 1 : limit;
    for (const event of [...list].sort(compareEvents)) {
      const free = day.slots.slice(0, usable).indexOf(null);
      if (free === -1) day.hidden += 1;
      else day.slots[free] = { kind: 'line', event };
    }
  });
  return { bars: shown, days };
}
